import { Typography } from '@vartanainc/design-system';
import { FormikProvider, useFormik, Form } from 'formik';
import { useContext, useEffect, useRef } from 'react';
import * as Yup from 'yup';
import { useReactiveVar } from '@apollo/client';
import { get } from 'lodash';
import { sessionVar } from '../../../../graphql/cache';
import TermsDropdown from '../../../../designSystem/CompactFields/TermsDropdown';
import CompactNumericField from '../../../../designSystem/CompactFields/CompactNumericField';
import { MAX_ALLOWED_TCV } from '../../../Orders/order.constants';
import { getTermOptionsForCustomerCreation } from '../../../../utils/helpers';
import { WidgetContext } from '../../../../context/WidgetContext';

interface FormValues {
  requestedAmount: number;
  requestedTerm: string;
}

interface WidgetContextType {
  selectedCompany: {
    formattedRequestedAmount: number;
    requestedTerm: string;
  };
  setCreateCreditCheckFormValues: (values: FormValues) => void;
}

function ConfirmPaymentOptions(): JSX.Element {
  const tcvFieldRef = useRef<HTMLInputElement>(null);
  const session = useReactiveVar(sessionVar);
  const termOptions = getTermOptionsForCustomerCreation(session);
  const { selectedCompany, setCreateCreditCheckFormValues } = useContext(
    WidgetContext
  ) as WidgetContextType;

  const validationSchema = Yup.object().shape({
    requestedAmount: Yup.number().required().positive().max(MAX_ALLOWED_TCV),
    requestedTerm: Yup.string().required(),
  });

  const defaultValues: FormValues = {
    requestedAmount: parseFloat(
      String(get(selectedCompany, 'formattedRequestedAmount', 0)).replace(/[$,]/g, '')
    ),
    requestedTerm: get(selectedCompany, 'requestedTerm', ''),
  };

  const formikBag = useFormik<FormValues>({
    initialValues: defaultValues,
    enableReinitialize: true,
    // disabling submit here as it is handled by the parent component
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
    validationSchema,
  });

  useEffect(() => {
    if (setCreateCreditCheckFormValues) {
      setCreateCreditCheckFormValues({
        requestedAmount: formikBag.values.requestedAmount,
        requestedTerm: formikBag.values.requestedTerm,
      });
    }
  }, [formikBag.values, setCreateCreditCheckFormValues]);

  return (
    <div className="w-full flex flex-col gap-4 justify-center items-start px-4 pt-4 pb-[18px] bg-white rounded-lg shadow-sm">
      <Typography variant="heading16" bold color="color-blue-180">
        Verify request
      </Typography>
      <FormikProvider value={formikBag}>
        <Form className="w-full">
          <div className="flex gap-2 w-full confirm-payment-options-container">
            <CompactNumericField
              label="Requested amount"
              customPrefix="$"
              name="requestedAmount"
              showHoverShadow={false}
              maxAllowedValue={MAX_ALLOWED_TCV}
              decimalScale={2}
              showInitialError={!!formikBag.errors.requestedAmount}
              onValueChange={(e) => {
                formikBag.setFieldValue('requestedAmount', parseFloat(e.value) || '');
              }}
              ref={tcvFieldRef}
            />
            <TermsDropdown
              name="requestedTerm"
              label="Requested term"
              id="requestedTerm"
              placeholder="Select term"
              options={termOptions}
              isCompact
              resetOptionsOnBlur
              dropdownOnly={false}
            />
          </div>
        </Form>
      </FormikProvider>
    </div>
  );
}

export default ConfirmPaymentOptions;
